export const DEV_APP_ENVS = ['development', 'staging', 'production', 'testing', 'testing2'] as const

export const DEV_LOCALES = [
  { id: 'ru', label: 'Русский' },
  { id: 'en', label: 'English' },
] as const

export const DEV_BACKENDS = [
  {
    id: 'staging',
    label: 'Staging · cue.rocks',
    proxyPath: '/cue.rocks/',
    BACKEND_URL: 'https://api.cue.rocks',
    S3_URL: 'https://be0756db-4680-4c21-93b8-4435a449e785.selstorage.ru',
  },
  {
    id: 'testing',
    label: 'Testing · cue.green',
    proxyPath: '/cue.green/',
    BACKEND_URL: 'https://api.cue.green',
    S3_URL: 'https://be0756db-4680-4c21-93b8-4435a449e785.selstorage.ru',
  },
  {
    id: 'testing2',
    label: 'Testing · cue-for.com',
    proxyPath: '/cue-for.com/',
    BACKEND_URL: 'https://api.cue-for.com',
    S3_URL: 'https://be0756db-4680-4c21-93b8-4435a449e785.selstorage.ru',
  },
  {
    id: 'production',
    label: 'Production · cue.business',
    proxyPath: '/cue.business/',
    BACKEND_URL: 'https://api.cue.business',
    S3_URL: 'https://e5eb07fc-33ec-4acf-b2c6-86ebd1a54910.selstorage.ru',
  },
  {
    id: 'local',
    label: 'Local · localhost:3000',
    proxyPath: '/__cue_dev_api/local/',
    BACKEND_URL: 'http://localhost:3000',
    S3_URL: 'http://localhost:3000',
  },
] as const

export interface DevEnvironment {
  appEnv?: (typeof DEV_APP_ENVS)[number]
  backend?: (typeof DEV_BACKENDS)[number]['id']
}

export function parseDevEnvironment(value: unknown): DevEnvironment {
  if (!value || typeof value !== 'object') {
    return {}
  }
  const settings = value as Record<string, unknown>
  return {
    appEnv: DEV_APP_ENVS.find(env => env === settings.appEnv),
    backend: DEV_BACKENDS.find(backend => backend.id === settings.backend)?.id,
  }
}

export function getDevEnvironmentOverrides({ appEnv, backend }: DevEnvironment) {
  const preset = DEV_BACKENDS.find(value => value.id === backend)
  return {
    ...(appEnv && { APP_ENV: appEnv }),
    ...(preset && { BACKEND_URL: preset.BACKEND_URL, S3_URL: preset.S3_URL }),
  }
}

export function getDevApiBaseURL({ backend }: DevEnvironment) {
  return DEV_BACKENDS.find(preset => preset.id === backend)?.proxyPath ?? '/api'
}

export function createDevApiProxies() {
  return Object.fromEntries(
    DEV_BACKENDS.map(backend => {
      const prefix = getDevApiBaseURL({ backend: backend.id })
      // The trailing slash matches a whole path segment and stays on the upstream URL.
      return [
        prefix,
        {
          target: backend.BACKEND_URL,
          changeOrigin: true,
          rewrite: (path: string) => path.slice(prefix.length - 1),
        },
      ] as const
    }),
  )
}

export function getApiEnvironmentTone(backendUrl: string): 'green' | 'coral' | 'gray' | undefined {
  try {
    const { hostname } = new URL(backendUrl)
    if (hostname === 'api.cue.green') {
      return 'green'
    }
    if (hostname === 'api.cue.rocks') {
      return 'coral'
    }
    if (hostname === 'api.cue-for.com') {
      return 'gray'
    }
  } catch {
    return undefined
  }
}

export function getApiEnvironmentLabel(backendUrl: string): 'PROD' | 'TEST' {
  try {
    return new URL(backendUrl).hostname === 'api.cue.business' ? 'PROD' : 'TEST'
  } catch {
    return 'TEST'
  }
}
